import { useState } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Plus, X } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

/**
 * AliasManager
 * ------------
 * Chip-style add/remove for an organisation's aliases. Saves immediately
 * via /api/admin/organisations/{id}/aliases.
 *
 * Props:
 *  - orgId
 *  - aliases: string[]
 *  - onChange(nextAliases)
 */
const AliasManager = ({ orgId, aliases = [], onChange }) => {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const res = await adminApi.addAlias(orgId, trimmed);
      onChange?.(res.data?.aliases || []);
      setDraft('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add alias');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (alias) => {
    setBusy(true);
    try {
      const res = await adminApi.removeAlias(orgId, alias);
      onChange?.(res.data?.aliases || []);
    } catch {
      toast.error('Could not remove alias');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5" data-testid="alias-manager">
      <div className="flex flex-wrap gap-1.5">
        {aliases.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            No aliases yet. Add common abbreviations or names users might type.
          </p>
        )}
        {aliases.map((a) => (
          <span
            key={a}
            className="inline-flex items-center gap-1 rounded-full bg-inset px-2.5 py-1 text-xs text-ink-70"
            data-testid={`alias-chip-${a}`}
          >
            {a}
            <button
              type="button"
              onClick={() => remove(a)}
              disabled={busy}
              className="rounded-full hover:bg-rule transition-colors duration-state p-0.5"
              aria-label={`Remove alias ${a}`}
              data-testid={`alias-remove-${a}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="e.g. BCC, Birmingham CC"
          className="flex-1"
          data-testid="alias-input"
        />
        <Button
          type="button"
          onClick={add}
          disabled={busy || !draft.trim()}
          size="sm"
          data-testid="alias-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
};

export default AliasManager;
